import { BtnClasses, DropDownMenu, IconButton } from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';
import { createMemo, type Component } from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';

type HeaderOptionKey =
  | 'sortAlphabetically'
  | 'refineNames'
  | 'addProperties'
  | 'managePropertySetLibraries';

interface HeaderOption {
  key: HeaderOptionKey;
  label: string;
}

export interface ObjectPropertiesPanelActionsProps {
  selectionCount: number;
  sortAlphabetically?: boolean;
  refineNames?: boolean;
  customPropertiesMutationAllowed?: boolean;
  onSortAlphabeticallyChanged?(sortAlphabetically: boolean): void;
  onRefineNamesChanged?(refineNames: boolean): void;
  onAddProperties?(): void;
  onManagePropertySetLibraries?(): void;
  onClose(): void;
}

export const ObjectPropertiesPanelActions: Component<
  ObjectPropertiesPanelActionsProps
> = (props) => {
  const headerOptions = createMemo<HeaderOption[]>(() => {
    const options: HeaderOption[] = [
      {
        key: 'sortAlphabetically',
        label: t('objectProperties.options.sortAlphabetically'),
      },
    ];

    if (props.selectionCount > 0) {
      options.push({
        key: 'refineNames',
        label: t('objectProperties.options.refineNames'),
      });
      options.push({
        key: 'addProperties',
        label: t('objectProperties.options.addProperties'),
      });
    }

    if (props.customPropertiesMutationAllowed === true) {
      options.push({
        key: 'managePropertySetLibraries',
        label: t('objectProperties.options.managePropertySetLibraries'),
      });
    }

    return options;
  });

  const headerOptionsSeparators = createMemo(() =>
    props.selectionCount > 0 ? [1] : undefined
  );

  const headerOptionsDisabledItems = createMemo(() => {
    return headerOptions().flatMap((option, index) => {
      if (option.key === 'sortAlphabetically' && props.selectionCount === 0) {
        return [index];
      }

      if (
        option.key === 'addProperties' &&
        (props.customPropertiesMutationAllowed !== true ||
          props.onAddProperties === undefined)
      ) {
        return [index];
      }

      return [];
    });
  });

  const disabledOptionsTooltipTitle = createMemo(() =>
    props.customPropertiesMutationAllowed === false && props.selectionCount > 0
      ? t('objectProperties.upgradeToUnlockFeature')
      : undefined
  );

  const selectedOptions = createMemo(() => {
    if (props.selectionCount === 0) return undefined;

    const labelsByKey = new Map<HeaderOptionKey, string>(
      headerOptions().map((option) => [option.key, option.label])
    );

    const selected: string[] = [];
    if (props.sortAlphabetically) {
      const sortLabel = labelsByKey.get('sortAlphabetically');
      if (sortLabel) {
        selected.push(sortLabel);
      }
    }
    if (props.refineNames) {
      const refineLabel = labelsByKey.get('refineNames');
      if (refineLabel) {
        selected.push(refineLabel);
      }
    }
    return selected.length > 0 ? selected : undefined;
  });

  const onOptionSelected = (_selected: string, selectedIndex: number): void => {
    const selectedOption = headerOptions()[selectedIndex];
    if (!selectedOption) return;

    switch (selectedOption.key) {
      case 'sortAlphabetically':
        props.onSortAlphabeticallyChanged?.(!props.sortAlphabetically);
        break;
      case 'refineNames':
        props.onRefineNamesChanged?.(!props.refineNames);
        break;
      case 'addProperties':
        props.onAddProperties?.();
        break;
      case 'managePropertySetLibraries':
        props.onManagePropertySetLibraries?.();
        break;
      default:
        throw new UnreachableCaseError(selectedOption.key);
    }
  };

  return (
    <div class="flex flex-row">
      <DropDownMenu
        // eslint-disable-next-line solid/no-react-specific-props
        className="mr-0"
        anchorIcon="icon-font tc-icon-more-vert"
        position="right"
        dropDownOptions={headerOptions().map((option) => option.label)}
        addSeparators={headerOptionsSeparators()}
        selected={selectedOptions()}
        onSelectionChanged={onOptionSelected}
        disabledItem={headerOptionsDisabledItems()}
        disabledOptionsTooltipTitle={disabledOptionsTooltipTitle()}
      />
      <IconButton
        color={BtnClasses.TERTIARY}
        icon="tc-icon-content-close"
        size="icon-medium"
        data-cy="object-properties-tab-close"
        clicked={props.onClose}
      />
    </div>
  );
};
